import can from 'can';
import _ from 'lodash';

import FeedService from 'scripts/services/base-feed-service';
import LocalStorageService from 'scripts/services/localstorage-service';

import template from './alert-manager.stache';
import appContext from 'scripts/app-context';
import PlayerService from 'scripts/services/player-service';
import elementResizeDetector from 'element-resize-detector';

import { isAlertUrl } from './alert-manager.controller';

const alertFeedUrl = window.globalSettings.feeds.smt.alertsUrl;

const isAlertsEnableKey = 'usgaIsAlertsEnable';
const closedAlertsKey = 'closedAlertsKey';
const DISABLE_ALERT_DELAY = 30 * 24 * 60 * 60 * 1000;
const CLOSED_ALERT_COUNT = 10;

export default can.Component.extend({
    tag: 'alert-manager',
    template,
    viewModel: {
        define: {
            alerts: {
                type: 'array',
                value: [],
            },
            currentAlert: {
                type: 'object',
                value: null,
            },
            playersList: {
                type: 'array',
                value: [],
            },
            showDisableAlert: {
                type: 'boolean',
                value: false,
            },
            isEnable: {
                type: 'boolean',
                value: true,
            },
            isMobile: {
                get() {
                    return (
                        ['extra-small'].indexOf(
                            appContext.model.screen.attr('screenSize'),
                        ) > -1
                    );
                },
            },
        },
        onCloseAlertClick() {
            this.attr('showDisableAlert', true);
        },
        onCloseDisableClick() {
            this.addCurrentIdToClosed();
            this.attr('showDisableAlert', false);
            this.getNextAlert();
        },
        onAlertsDisable() {
            this.attr('isEnable', false);
            LocalStorageService.setItem(isAlertsEnableKey, Date.now());
        },
        onAlertHide() {
            this.addCurrentIdToClosed();
            this.getNextAlert();
        },
        getNextAlert() {
            const [alert, ...restAlerts] = this.attr('alerts').serialize();

            if (alert && this.isEnable) {
                this.attr('currentAlert', alert);
                this.attr('alerts', restAlerts);
            } else {
                this.attr('currentAlert', null);
            }
        },
        getClosedAlertIds() {
            const closedAlerts = LocalStorageService.getItem(closedAlertsKey);

            return JSON.parse(closedAlerts || '[]');
        },
        addCurrentIdToClosed() {
            const currentId =
                this.attr('currentAlert') && this.attr('currentAlert').id;

            if (!currentId) {
                return;
            }

            const closedAlerts = this.getClosedAlertIds();

            if (closedAlerts.length >= CLOSED_ALERT_COUNT) {
                closedAlerts.shift();
            }

            closedAlerts.push(currentId);

            LocalStorageService.setItem(closedAlertsKey, closedAlerts);
        },
    },
    events: {
        inserted() {
            const closedIds = this.viewModel.getClosedAlertIds();
            this.addedIds = new Set(closedIds);
            this.alertManager = this.element.find('.c-alert-manager');
            this.headerPlaceholder = can.$('.m-header .s-header-place-holder');
            this.resizeDetector = elementResizeDetector();

            const disableTimestamp = LocalStorageService.getItem(
                isAlertsEnableKey,
            );

            const isEnable =
                !Boolean(disableTimestamp) ||
                disableTimestamp + DISABLE_ALERT_DELAY <= Date.now();

            if (isEnable && disableTimestamp) {
                LocalStorageService.removeItem(isAlertsEnableKey);
            }

            this.viewModel.attr('isEnable', isEnable);

            if (isEnable) {
                this.viewModel.bind('alerts', (ev, value) => {
                    const alerts = value.serialize();

                    if (
                        Array.isArray(alerts) &&
                        this.viewModel.currentAlert === null
                    ) {
                        this.viewModel.getNextAlert();
                    }
                });

                this.createFeedService();
            }

            this.checkOffsetTop();
            this.checkScroll();
            this.createModelListeners();
        },
        createModelListeners() {
            appContext.model.screen.bind('scrollOffset', () =>
                this.checkScroll(),
            );
            this.resizeDetector.listenTo(
                this.headerPlaceholder,
                ({ offsetHeight }) => this.checkOffsetTop(offsetHeight),
            );
        },
        checkOffsetTop(offsetHeight) {
            const offsetTop = offsetHeight || this.headerPlaceholder.height();
            if (!this.viewModel.attr('isMobile') || !offsetTop) {
                return this.alertManager.css({ top: '' });
            }

            this.alertManager.css({ top: offsetTop });
        },
        checkScroll: function() {
            const scrollOffset = appContext.model.screen.attr('scrollOffset');

            const hamburger = document.querySelector(
                '.m-hamburger-menu.is-expanded',
            );

            if (hamburger) {
                return;
            }

            this.alertManager
                .toggleClass('is-scrolled', scrollOffset > 80)
                .attr(
                    'data-scroll-direction',
                    appContext.model.screen.attr('scrollDirection'),
                );
        },
        createFeedService() {
            PlayerService.getList((__, playersList) => {
                this.viewModel.attr('playersList', playersList);
            });

            this.feedService = new FeedService(alertFeedUrl, false, true, 10000)
                .on('data', (__, data) => {
                    this.addAlerts(_.get(data, 'alerts', []));
                })
                .on('error', () => {
                    this.addAlerts([]);
                });

            this.feedService.fetch();
        },
        getGenericDataByAlertType(type) {
            let text;

            switch (type) {
                case 'liveVideo':
                case 'liveStream':
                    text =
                        'Multiple favorited players are now featured on the live stream.';
                    break;
                case 'videoCard':
                    text =
                        'Multiple favorited players are featured in a new video on demand.';
                    break;
                case 'photoCard':
                    text =
                        'Multiple favorited players are featured in a new photo gallery.';
                    break;
                case 'articleCard':
                    text =
                        'Multiple favorited players are featured in a new article.';
                    break;
                default:
                    return {};
            }

            return {
                image: {
                    type: 'imageCloudinary',
                    // TODO: Move this identifier to AEM configuration
                    identifier: '/us-open/logos/torrey-pines.png',
                },
                text,
            };
        },
        addAlerts(alerts) {
            const favoriteIds = PlayerService.getFavorites();
            const showTypes = ['leaderFinish', 'soleLeader', 'ace'];

            const filteredAlerts = alerts.filter((alert) => {
                return (
                    !this.addedIds.has(alert.id) &&
                    !isAlertUrl(window.location.toString(), alert.button.url) &&
                    (showTypes.includes(alert.type) ||
                        favoriteIds.indexOf(alert.playerId) >= 0)
                );
            });

            const alertGroups = filteredAlerts.reduce((memo, item) => {
                if (memo[item.button.url]) {
                    memo[item.button.url].push(item);
                } else {
                    memo[item.button.url] = [item];
                }

                return memo;
            }, {});

            const newAlerts = Object.values(alertGroups).map((group) => {
                const item = group[0];

                return Object.assign(
                    {
                        image: {
                            type: 'imageGeneric',
                            url: item.playerImage,
                        },
                    },
                    item,
                    group.length > 1
                        ? this.getGenericDataByAlertType(item.type)
                        : {},
                );
            });

            this.viewModel.attr('alerts', [
                ..._.get(this.viewModel, 'alerts', []),
                ...newAlerts,
            ]);

            alerts.forEach((alert) => this.addedIds.add(alert.id));
        },
    },
});
